import { useState, useRef, useEffect } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeAll = () => {
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
    setIsOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a
            href="/"
            className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 tracking-tight hover:opacity-80 transition-opacity whitespace-nowrap"
          >
            Itaobuy<span className="italic font-normal text-primary-500">Spreadsheet</span>
          </a>

          <div className="hidden md:flex items-center gap-6">
            <a href="/" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap font-medium">
              <i className="ri-home-4-line w-4 h-4 inline-flex items-center justify-center mr-1 align-text-bottom"></i>
              Home
            </a>
            <a href="/itaobuy-spreadsheet-guida-acquisti" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">
              <i className="ri-shopping-bag-3-line w-4 h-4 inline-flex items-center justify-center mr-1 align-text-bottom"></i>
              Guida Acquisti
            </a>
            <a href="/itaobuy-spreadsheet-guida" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">
              Guida
            </a>
            <a href="/itaobuy-spreadsheet-recensione" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">
              Recensioni
            </a>
            <a href="/itaobuy-spreadsheet-sicurezza" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">
              Sicurezza
            </a>
            <a href="/itaobuy-spreadsheet-confronto" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">
              Confronti
            </a>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-background-300 bg-white/90 text-sm text-foreground-700 hover:border-primary-300 transition-all whitespace-nowrap cursor-pointer"
              >
                <span>Categorie Prodotto</span>
                <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-background-200 py-2 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                      onClick={() => setIsOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-background-100 px-6 py-5 max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col gap-1">
            <a href="/" onClick={closeAll} className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground-700 rounded-lg hover:bg-background-100 transition-colors">
              <i className="ri-home-4-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
              Home
            </a>
            <a href="/itaobuy-spreadsheet-guida-acquisti" onClick={closeAll} className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground-700 rounded-lg hover:bg-background-100 transition-colors">
              <i className="ri-shopping-bag-3-line w-4 h-4 flex items-center justify-center text-accent-500"></i>
              Guida Acquisti
            </a>
            <a href="/itaobuy-spreadsheet-guida" onClick={closeAll} className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground-700 rounded-lg hover:bg-background-100 transition-colors">
              <i className="ri-book-open-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
              Guida Completa
            </a>
            <a href="/itaobuy-spreadsheet-recensione" onClick={closeAll} className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground-700 rounded-lg hover:bg-background-100 transition-colors">
              <i className="ri-star-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
              Recensioni
            </a>
            <a href="/itaobuy-spreadsheet-sicurezza" onClick={closeAll} className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground-700 rounded-lg hover:bg-background-100 transition-colors">
              <i className="ri-shield-check-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
              Sicurezza
            </a>
            <a href="/itaobuy-spreadsheet-confronto" onClick={closeAll} className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground-700 rounded-lg hover:bg-background-100 transition-colors">
              <i className="ri-scales-3-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
              Confronti
            </a>

            <div className="border-t border-background-100 mt-2 pt-3">
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between w-full px-3 py-2.5 text-sm font-semibold text-foreground-800 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-2.5">
                  <i className="ri-grid-line w-4 h-4 flex items-center justify-center text-secondary-500"></i>
                  Categorie Prodotto
                </span>
                <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {mobileCatOpen && (
                <div className="mt-1 ml-9 grid grid-cols-2 gap-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="px-3 py-2 text-xs text-foreground-600 rounded-lg hover:bg-background-100 hover:text-primary-500 transition-colors whitespace-nowrap"
                      onClick={closeAll}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="mt-3 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium text-center hover:bg-primary-600 transition-colors"
              onClick={closeAll}
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}